import { useEffect, useState, useRef, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { computeUserTier } from '../lib/trialCheck';
import type { User } from '@supabase/supabase-js';
import { getLocalDevAuth } from '../lib/localDevAuth';

type Status = 'loading' | 'no-auth' | 'subscribed' | 'trial-active' | 'trial-expired' | 'payment-polling';

const PAYMENT_POLL_INTERVAL_MS = 2000;
const PAYMENT_POLL_MAX_ATTEMPTS = 15; // 30 seconds total

export function AuthGuard({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<Status>('loading');
  const [user, setUser] = useState<User | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearPaymentParam = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('payment_success')) {
      params.delete('payment_success');
      const url = new URL(window.location.href);
      url.search = params.toString();
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  // Clean up polling timer on unmount
  useEffect(() => {
    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    let dead = false;

    const check = async () => {
      const localDevAuth = getLocalDevAuth();
      if (localDevAuth) {
        setUser(localDevAuth.user as User);
        setStatus('trial-active');
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (dead) return;
      if (!session) { setStatus('no-auth'); return; }

      setUser(session.user);

      const { data: profile } = await supabase
        .from('profiles')
        .select('subscribed, trial_expires_at')
        .eq('id', session.user.id)
        .single();

      if (!dead && profile) {
        const tier = computeUserTier(profile);

        // If trial-expired but user has ?payment_success=true, start polling
        if (tier === 'trial-expired') {
          const params = new URLSearchParams(window.location.search);
          if (params.get('payment_success') === 'true') {
            setStatus('payment-polling');
            startPaymentPoll(session.user.id);
            return;
          }
        }

        setStatus(tier);
      } else if (!dead) {
        setStatus('trial-expired');
      }
    };

    const startPaymentPoll = (userId: string) => {
      let attempts = 0;

      const poll = async () => {
        if (dead) return;
        attempts++;

        try {
          const { data: profile } = await supabase
            .from('profiles')
            .select('subscribed, trial_expires_at')
            .eq('id', userId)
            .single();

          if (dead) return;

          if (profile && computeUserTier(profile) !== 'trial-expired') {
            // Payment confirmed — let user through
            if (pollTimerRef.current) clearInterval(pollTimerRef.current);
            clearPaymentParam();
            setStatus(computeUserTier(profile));
            return;
          }
        } catch {
          // Poll continues even on error — transient network issues
        }

        if (attempts >= PAYMENT_POLL_MAX_ATTEMPTS) {
          // Timeout — redirect to pricing with message
          if (pollTimerRef.current) clearInterval(pollTimerRef.current);
          clearPaymentParam();
          setStatus('trial-expired');
        }
      };

      // First poll immediately, then on interval
      poll();
      pollTimerRef.current = setInterval(poll, PAYMENT_POLL_INTERVAL_MS);
    };

    check();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      // Only re-check on sign-in/sign-out events — NOT on token refresh.
      // Token refresh is a background operation that shouldn't trigger a full
      // auth + profile re-check (which can race with getSession() returning null).
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
        // Stop any active polling before re-checking
        if (pollTimerRef.current) {
          clearInterval(pollTimerRef.current);
          pollTimerRef.current = null;
        }
        check();
      }
    });
    return () => { dead = true; subscription.unsubscribe(); };
  }, [clearPaymentParam]);

  if (status === 'loading') return null;
  if (status === 'no-auth') return <Navigate to="/login" replace />;
  if (status === 'payment-polling') {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600 mx-auto mb-4" />
          <p className="text-gray-600 text-sm">Processing your payment...</p>
          <p className="text-gray-400 text-xs mt-1">This should only take a moment.</p>
        </div>
      </div>
    );
  }
  if (status === 'trial-expired') {
    return <Navigate to="/pricing?trial_expired=true" replace />;
  }
  // Both 'subscribed' and 'trial-active' pass through
  return <>{children}</>;
}
